import React, { useRef, useState } from "react";
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../CommonComponents/ThemeProvider';
import { Minus, PencilLine, Plus, X } from "lucide-react";

export default function EditStockModal({ isOpen, onClose, inventory, onSave, selectedBranchId = "" }) {
    const scopedQuantity = selectedBranchId
        ? (Number(inventory?.released_quantity) || 0)
        : (Number(inventory?.quantity) || 0);
    const stockLabel = selectedBranchId ? "Stocks Released" : "Stocks Available";
    const [quantity, setQuantity] = useState(scopedQuantity);
    const quantityInputRef = useRef(null);
    const { currentTheme } = useTheme();

    React.useEffect(() => {
        if (isOpen && inventory) {
            setQuantity(scopedQuantity);
        }
    }, [isOpen, inventory, scopedQuantity]);

    React.useEffect(() => {
        if (!isOpen || !inventory) return;

        const focusQuantityInput = () => {
            quantityInputRef.current?.focus({ preventScroll: true });
            quantityInputRef.current?.select();
        };

        const frameId = window.requestAnimationFrame(focusQuantityInput);
        const timeoutId = window.setTimeout(focusQuantityInput, 80);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.clearTimeout(timeoutId);
        };
    }, [isOpen, inventory, selectedBranchId]);

    if (!isOpen) return null;
    const numericQuantity = Math.max(Number(quantity) || 0, 0);
    const adjustQuantity = (delta) => setQuantity(Math.max(numericQuantity + delta, 0));

    const handleSave = () => {
        onSave(numericQuantity);
    };

    const handleQuantityKeyDown = (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        handleSave();
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-slate-950/45 px-4 backdrop-blur-sm"
        >
            <div className="w-full max-w-md">
                <Dialog.Panel className="relative flex max-h-[90vh] flex-col overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.05),rgba(148,163,184,0.04),transparent)]" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-500 shadow-sm transition hover:text-slate-900"
                        aria-label="Close edit stock modal"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative shrink-0 px-6 pb-4 pt-6 md:px-7 md:pt-7">
                        <div className="flex items-start gap-4 pr-12">
                            <div className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: currentTheme.color }}>
                                <PencilLine className="h-6 w-6" />
                            </div>
                            <div className="min-w-0">
                                <Dialog.Title className="text-2xl font-semibold tracking-tight text-slate-950">
                                    Edit Stock
                                </Dialog.Title>
                                <p className="mt-1 text-sm leading-6 text-slate-500">
                                    Update the selected inventory quantity with a clear, exact value.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="relative min-h-0 overflow-y-auto overflow-x-hidden px-6 pb-6 md:px-7 md:pb-7">
                        <div className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-4 text-left">
                            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">{stockLabel}</label>
                            <div
                                className="flex h-16 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition focus-within:ring-4"
                                style={{
                                    boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
                                    "--tw-ring-color": "color-mix(in srgb, var(--color-primary) 14%, transparent)",
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => adjustQuantity(-1)}
                                    disabled={numericQuantity <= 0}
                                    className="flex w-14 shrink-0 items-center justify-center border-r border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40"
                                    aria-label={`Decrease ${stockLabel.toLowerCase()}`}
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <input
                                    ref={quantityInputRef}
                                    type="number"
                                    className="no-number-spinner min-w-0 flex-1 border-0 bg-white px-3 text-center text-lg font-semibold text-slate-900 outline-none"
                                    value={quantity}
                                    min={0}
                                    step={1}
                                    inputMode="numeric"
                                    onKeyDown={handleQuantityKeyDown}
                                    onChange={e => setQuantity(e.target.value === "" ? "" : Number(e.target.value))}
                                    placeholder={`Enter new ${stockLabel.toLowerCase()} quantity`}
                                />
                                <button
                                    type="button"
                                    onClick={() => adjustQuantity(1)}
                                    className="flex w-14 shrink-0 items-center justify-center border-l border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                                    aria-label={`Increase ${stockLabel.toLowerCase()}`}
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                onClick={onClose}
                                className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                className="rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90 focus:outline-none"
                                style={{ background: `linear-gradient(135deg, ${currentTheme.color}, color-mix(in srgb, ${currentTheme.color} 78%, black))` }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
